import { __awaiter } from "tslib";
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, MessageBar, MessageBarType, Panel, PanelType, Spinner, SpinnerSize, Stack, Text } from '@fluentui/react';
import FMForecastView from './FMForecastView/FMForecastView';
import DSECapacityHoursPanel from './DSECapacityHours/DSECapacityHoursPanel';
import FMAddEventPanel from './FMAddEvent/FMAddEventPanel';
import { buildNonProjectAssignment } from './FMAddEvent/fmAddEventPayload';
import FMDashboardService from '../../services/FMDashboard.service';
import FMOneProfileService from '../../services/FMOneProfile.service';
import ManagerTypeahead from './ManagerTypeahead/ManagerTypeahead';
import FMBreadcrumb from './ManagerTypeahead/FMBreadcrumb';
import { useFMDashboardData } from './hooks/useFMDashboardData';
import { RESOURCE_CALENDAR_STATE, RESOURCE_SEARCH_HREF, RESOURCE_SEARCH_STATE, resourceCalendarLink, resourceProfileHref, } from './shared/fmLinks';
import { getFMPermissions } from './shared/fmAuthorization';
import { getCurrentManagerAlias, getCurrentManagerName } from './shared/fmUser';
import { navigateToState, openStateInNewTab, openUrlInNewTab } from './shared/fmNavigation';
import { RAvailUrlProvider } from '../../../common/RAvailUrlProvider';
/**
 * FM Dashboard page (F1/F2/F7/F8/F9) — the FXP route component for the FM team forecast screen.
 * Loads live GRM data (GetTeamCalendar + master data) plus RAvail DSE capacity hours via
 * `useFMDashboardData`. Composes the manager typeahead + breadcrumb header with `FMForecastView`,
 * owns month navigation, opens the resource profile/calendar links in a new tab (parity of the GRM
 * links) and hosts the DSE Capacity Hours editor in a side panel.
 */
/** Dev override: `#/fm-dashboard?manager=<alias>` forces the team we load (for local testing). */
const managerFromHash = () => {
    try {
        const hash = window.location.hash || '';
        const q = hash.indexOf('?') > -1 ? hash.slice(hash.indexOf('?') + 1) : '';
        const alias = new URLSearchParams(q).get('manager');
        return alias ? alias.toLowerCase() : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
const startOfThisMonth = () => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
};
/**
 * Root crumb for the signed-in FM's own team. Legacy labelled this header
 * `name + "'s TEAM"` (`CreateBreadcrumb` — fmdashboard.controller.js:158) and uppercased it in the
 * template (`fmdashboard.html:6` `{{managerAlias.toUpperCase()}}`). When the FXP shell can't
 * resolve a name (tests / local preview) we fall back to the neutral "My Team".
 */
const makeRootCrumb = () => {
    const name = getCurrentManagerName();
    return { key: 'me', text: name ? `${name}'s Team` : 'My Team' };
};
/** Open a hash route in a new tab (parity: the GRM profile/calendar links are `target="_blank"`). */
const openInNewTab = (href) => openUrlInNewTab(href);
const FMDashboardPage = ({ initialMonthStart, permissions, onSearchManagers, dsePanelServices, useSampleData, }) => {
    var _a;
    const [managerAlias, setManagerAlias] = useState(managerFromHash());
    /** Set once the user picks a team themselves, so the hash sync below stops overriding them. */
    const managerPickedRef = useRef(false);
    const [monthStart, setMonthStart] = useState(initialMonthStart !== null && initialMonthStart !== void 0 ? initialMonthStart : startOfThisMonth());
    const [reloadToken, setReloadToken] = useState(0);
    const reloadTokenRef = useRef(0);
    const data = useFMDashboardData({
        managerAlias,
        monthStart,
        reloadToken,
        useSample: useSampleData,
    });
    const isLiveData = data.source === 'live';
    const isSampleData = data.source === 'sample';
    const isUnavailable = data.source === 'unavailable';
    const [dseRow, setDseRow] = useState(null);
    const [capacityOverrides, setCapacityOverrides] = useState({});
    const signedInAlias = useMemo(() => getCurrentManagerAlias(), []);
    const [crumbs, setCrumbs] = useState(() => [makeRootCrumb()]);
    // Add-Event flyout state (F6).
    const [addTarget, setAddTarget] = useState(null);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [addError, setAddError] = useState(undefined);
    const [successMsg, setSuccessMsg] = useState(undefined);
    const requestReload = () => {
        const nextToken = reloadTokenRef.current + 1;
        reloadTokenRef.current = nextToken;
        setReloadToken(nextToken);
        return nextToken;
    };
    /**
     * Re-read `?manager=<alias>` after the URL has settled.
     *
     * The FXP/AngularJS router flushes `window.location.hash` at the END of the digest that renders
     * this component (and sticky states can keep it mounted across transitions), so the mount-time
     * read above can still see the PREVIOUS route and silently drop the alias — the page then loads
     * the signed-in user's own team instead of the requested manager's. Re-read once the URL has
     * settled and on every hash change, but stop as soon as the user picks a team themselves.
     */
    useEffect(() => {
        const syncFromHash = () => {
            if (managerPickedRef.current) {
                return;
            }
            const fromHash = managerFromHash();
            if (fromHash) {
                setManagerAlias((current) => (current === fromHash ? current : fromHash));
                // Keep the header honest about WHOSE team is on screen. Without this the page loads the
                // deep-linked manager's team while the heading still reads the signed-in user's own name.
                // We only have the alias at this point, so label the crumb with it; picking a manager
                // through the typeahead replaces it with the resolved display name.
                setCrumbs((current) => {
                    if (fromHash === signedInAlias) {
                        return current;
                    }
                    const last = current[current.length - 1];
                    if (last && last.alias === fromHash) {
                        return current;
                    }
                    // `CreateBreadcrumb` semantics: a deep link identifies ONE team, so reset the trail to a
                    // single crumb rather than appending under the signed-in user. Two crumbs would render
                    // the breadcrumb trail on top of the label and stack the team names.
                    return [{ key: fromHash, text: `${fromHash}'s Team`, alias: fromHash }];
                });
            }
        };
        const timer = window.setTimeout(syncFromHash, 0);
        window.addEventListener('hashchange', syncFromHash);
        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('hashchange', syncFromHash);
        };
    }, [signedInAlias]);
    const openAddEvent = (row, date) => {
        if (!isLiveData) {
            return;
        }
        setAddError(undefined);
        setSuccessMsg(undefined);
        setAddTarget({ row, date });
    };
    const closeAddEvent = () => setAddTarget(null);
    const submitAddEvent = (form) => __awaiter(void 0, void 0, void 0, function* () {
        if (!addTarget) {
            return;
        }
        if (!isLiveData) {
            setAddError('Events cannot be created until live FM data is available.');
            return;
        }
        setAddSubmitting(true);
        setAddError(undefined);
        try {
            const payload = buildNonProjectAssignment(form, data.addEventTypes, data.technologies, addTarget.row.rowId);
            yield FMDashboardService.getSingleton().createNonProjectRequest(payload);
            setAddTarget(null);
            setSuccessMsg('Event added successfully.');
            // Force the team calendar to re-fetch so the new event shows.
            requestReload();
        }
        catch (e) {
            setAddError(e instanceof Error ? e.message : 'Error adding an event. Please try again.');
        }
        finally {
            setAddSubmitting(false);
        }
    });
    useEffect(() => {
        if (data.loading) {
            return;
        }
        setCapacityOverrides((current) => {
            let changed = false;
            const next = {};
            Object.keys(current).forEach((alias) => {
                var _a;
                const value = current[alias];
                const authoritativeRead = data.source === 'live' &&
                    ((_a = data.capacityReadByAlias[alias.toLowerCase()]) === null || _a === void 0 ? void 0 : _a.status) === 'loaded';
                if (authoritativeRead &&
                    data.loadedReloadToken >= value.refreshToken) {
                    changed = true;
                }
                else {
                    next[alias] = value;
                }
            });
            return changed ? next : current;
        });
    }, [
        data.loading,
        data.loadedReloadToken,
        data.source,
        data.capacityReadByAlias,
    ]);
    const displayRows = data.rows.map((r) => {
        const capacityOverride = capacityOverrides[r.rowId];
        return capacityOverride
            ? Object.assign(Object.assign({}, r), { contractHours: capacityOverride.hours }) : r;
    });
    // Only highlight "today" while the visible month IS the current month.
    const now = new Date();
    const todayDate = data.monthStart.getFullYear() === now.getFullYear() && data.monthStart.getMonth() === now.getMonth()
        ? now.getDate()
        : undefined;
    const shiftMonth = (delta) => setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
    const closePanel = () => setDseRow(null);
    const handleSaved = (hours, savedAlias) => {
        const refreshToken = requestReload();
        setCapacityOverrides((current) => (Object.assign(Object.assign({}, current), { [savedAlias]: { hours, refreshToken } })));
        setDseRow((current) => current &&
            current.rowId.toLowerCase() === savedAlias.toLowerCase()
            ? null
            : current);
        // Legacy re-fetches the month after a successful save rather than trusting the local value
        // (fmdashboard.controller.js:108-141). The override above keeps the cell correct until the
        // authoritative refetch lands.
    };
    // Cross-team link gating (parity: fmdashboard.controller.js:62-71, 204-216). Default-deny —
    // a link to another team's screen is only rendered when the user's claims allow following it.
    const perms = useMemo(() => permissions !== null && permissions !== void 0 ? permissions : getFMPermissions(), [permissions]);
    const esxpBaseUrl = useMemo(() => {
        try {
            return new RAvailUrlProvider().esxpBaseUrl();
        }
        catch (_a) {
            return undefined;
        }
    }, []);
    const searchManagers = useMemo(() => onSearchManagers !== null && onSearchManagers !== void 0 ? onSearchManagers : ((q) => FMOneProfileService.getSingleton().searchManagers(q)), [onSearchManagers]);
    /**
     * Build the resource-calendar link for a row, or `undefined` when it cannot be built correctly
     * (no role from claims, or no resolved manager alias — e.g. explicit sample mode). We hide
     * the link rather than emit a two-segment URL, which resolves to a DIFFERENT registered state
     * (`grm-resourcedashboardquicksearch`).
     */
    const calendarLinkFor = (alias) => resourceCalendarLink(alias, {
        managerAlias: data.managerAlias,
        role: perms.calendarRole,
    });
    const openResourceCalendar = (alias) => {
        const link = calendarLinkFor(alias);
        if (link) {
            openStateInNewTab(RESOURCE_CALENDAR_STATE, link.params, link.fallbackUrl);
        }
    };
    const openResourceSearch = (e) => {
        // Navigate by FXP state name so the skillsearch team stays free to change its own URL.
        if (navigateToState(RESOURCE_SEARCH_STATE)) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
        }
    };
    const selectedCapacityOverride = dseRow
        ? capacityOverrides[dseRow.rowId]
        : undefined;
    const selectedCapacityRead = dseRow
        ? data.capacityReadByAlias[dseRow.rowId.toLowerCase()]
        : undefined;
    const selectedInitialHours = dseRow
        ? (_a = selectedCapacityOverride === null || selectedCapacityOverride === void 0 ? void 0 : selectedCapacityOverride.hours) !== null && _a !== void 0 ? _a : dseRow.contractHours : undefined;
    const capacityValueEstablished = !isLiveData
        ? selectedInitialHours !== undefined
        : (selectedCapacityRead === null || selectedCapacityRead === void 0 ? void 0 : selectedCapacityRead.status) === 'loaded' ||
            (selectedCapacityRead === null || selectedCapacityRead === void 0 ? void 0 : selectedCapacityRead.status) === 'missing';
    const capacityReadOnly = !isLiveData || !capacityValueEstablished;
    const capacityReadOnlyMessage = !isLiveData
        ? isSampleData
            ? 'Capacity hours cannot be edited while sample data is shown.'
            : 'Capacity hours cannot be edited until live FM data is available.'
        : (selectedCapacityRead === null || selectedCapacityRead === void 0 ? void 0 : selectedCapacityRead.message) ||
            'Capacity hours cannot be edited until a successful refresh establishes the current value.';
    return (React.createElement(Stack, { tokens: { childrenGap: 12 }, styles: { root: { padding: 16 } }, "data-testid": "fm-dashboard-page" },
        crumbs.length > 1 ? (React.createElement(FMBreadcrumb, { items: crumbs, onNavigate: (it) => {
                setCrumbs((cs) => cs.slice(0, cs.findIndex((c) => c.key === it.key) + 1));
                managerPickedRef.current = true;
                // Back to "My Team" clears the override (reloads current user's team); else load that manager.
                if (it.key === 'me') {
                    setManagerAlias(undefined);
                }
                else if (it.alias) {
                    setManagerAlias(it.alias);
                }
            } })) : null,
        React.createElement(Stack, { horizontal: true, horizontalAlign: "space-between", verticalAlign: "center", wrap: true, tokens: { childrenGap: 12 }, styles: { root: { padding: '5px 10px', margin: '2px 0 0 0' } } },
            React.createElement(Text, { as: "div", role: "heading", "aria-level": 1, "data-testid": "fm-title", styles: {
                    root: {
                        textTransform: 'uppercase',
                        color: '#0078d4',
                        fontWeight: 500,
                        fontSize: 14,
                        margin: 0,
                    },
                } }, crumbs[crumbs.length - 1].text),
            React.createElement(ManagerTypeahead, { label: "View Other Manager's Dashboard", onSearch: searchManagers, onSelect: (m) => {
                    managerPickedRef.current = true;
                    setManagerAlias(m.alias);
                    // Parity with legacy `CreateBreadcrumb` (fmdashboard.controller.js:152-159), which
                    // the typeahead's onSelect calls (:1831). It does `Breadcrumbs = []` then pushes ONE
                    // entry — so the trail is RESET to a single crumb, not appended to. The template only
                    // renders the trail when `Breadcrumbs.length > 1` (fmdashboard.html:31), so after a
                    // typeahead pick legacy shows just the label. Keeping `rootCrumb` here produced two
                    // crumbs, which rendered the trail AND the label — the stacked team names.
                    setCrumbs([{ key: m.alias, text: `${m.name}'s Team`, alias: m.alias }]);
                } })),
        perms.hasResourceSearchAccess ? (React.createElement(Stack, { horizontal: true, horizontalAlign: "end", styles: { root: { padding: '0 10px', margin: '0 0 15px 0' } } },
            React.createElement(Link, { href: RESOURCE_SEARCH_HREF, onClick: openResourceSearch, "data-testid": "fm-resource-search" }, "Resource Search"))) : null,
        data.source === 'sample' ? (React.createElement(MessageBar, { messageBarType: MessageBarType.info, "data-testid": "fm-sample-banner" }, "Showing explicit sample data for component testing.")) : null,
        isUnavailable && !data.loading ? (React.createElement(MessageBar, { messageBarType: MessageBarType.error, "data-testid": "fm-load-error" }, data.error || 'The FM dashboard could not be loaded.')) : null,
        successMsg ? (React.createElement(MessageBar, { messageBarType: MessageBarType.success, onDismiss: () => setSuccessMsg(undefined), "data-testid": "fm-add-event-success" }, successMsg)) : null,
        data.loading ? (React.createElement(Spinner, { size: SpinnerSize.large, label: "Loading team calendar\u2026", "data-testid": "fm-loading" })) : isUnavailable ? null : data.rows.length === 0 ? (React.createElement(MessageBar, { messageBarType: MessageBarType.info, "data-testid": "fm-empty-team" }, "No team calendar data was returned for this manager.")) : (React.createElement(FMForecastView, { rows: displayRows, days: data.days, monthLabel: data.monthLabel, todayDate: todayDate, eventTypes: data.addEventTypes, exportResources: data.exportResources, exportMonthDate: data.monthStart, onPrevMonth: () => shiftMonth(-1), onNextMonth: () => shiftMonth(1), onOpenName: (alias) => openInNewTab(resourceProfileHref(alias, esxpBaseUrl)), nameHref: (alias) => resourceProfileHref(alias, esxpBaseUrl), onOpenCalendar: openResourceCalendar, calendarHref: (alias) => { var _a; return (_a = calendarLinkFor(alias)) === null || _a === void 0 ? void 0 : _a.fallbackUrl; }, onOpenDSE: setDseRow, onAddEvent: openAddEvent })),
        dseRow ? (React.createElement(DSECapacityHoursPanel, { isOpen: true, alias: dseRow.rowId, resourceName: dseRow.name, initialHours: selectedInitialHours, onSaved: handleSaved, onCancel: closePanel, readOnly: capacityReadOnly, readOnlyMessage: capacityReadOnlyMessage, capacityValueEstablished: capacityValueEstablished, capacityErrorMessage: isLiveData ? capacityReadOnlyMessage : undefined, isSampleData: isSampleData, profileHref: resourceProfileHref(dseRow.rowId, esxpBaseUrl), services: dsePanelServices })) : null,
        React.createElement(Panel, { isOpen: !!addTarget, onDismiss: closeAddEvent, type: PanelType.medium, headerText: "Add an Event", closeButtonAriaLabel: "Close", isLightDismiss: !addSubmitting }, addTarget ? (React.createElement(FMAddEventPanel, { resourceAlias: addTarget.row.rowId, resourceName: addTarget.row.name, initialDate: addTarget.date, defaultTimeZone: addTarget.row.timeZone, eventTypes: data.eventTypes, technologies: data.technologies, submitting: addSubmitting, error: addError, onSubmit: submitAddEvent, onCancel: closeAddEvent })) : null)));
};
export default FMDashboardPage;
//# sourceMappingURL=FMDashboardPage.js.map